/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 31/05/24
 */

import { Tile } from '@/components/Tile';
import { Box, Button, Flex, Input, Stack, Text, useToast } from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    withCurrentAssignment,
    WithCurrentAssignmentProps,
} from '@/components/contexts/AssignmentContext/withCurrentAssignment';
import { AttachmentIcon } from '@chakra-ui/icons';
import { useDeleteRequest, useGetRequest, usePostRequest } from '@/utils/useStatefulRequests';
import { Holiday } from '@/wpt-lib/interfaces/Holiday';
import { LoadingSpinner } from '@/components';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { Column, ListTable } from '@/components/pages/dashboard/tiles/bottomTile/bottomTileTables/ListTable';
import { defaultTableParams } from '@/utils/tableUtils';
import { PaginationToolbar } from '@/components/Table';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import Icons from '@/components/icons/Icons';
import { AxiosError } from 'axios';
import { Dialog } from '@/components/Dialog';

const HolidayTableColumns: Column<Holiday>[] = [
    {
        name: 'Date',
        getData: (Holiday) => `${Holiday.date}`,
    },
    {
        name: 'Description',
        getData: (Holiday) => Holiday.description ?? '-',
    },
];

const UploadHolidays = ({ currentAssignment }: WithCurrentAssignmentProps) => {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(defaultTableParams.page);
    const [pages, setPages] = useState(1);
    const [deleteHolidayDate, setDelete] = useState<Holiday | undefined>(undefined);
    const [focusPage, setFocusPage] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();

    const {
        makeRequest: uploadHoliday,
        error: uploadError,
        loading: uploadLoading,
    } = usePostRequest<Holiday[], FormData>();

    const { makeRequest: removeHolidayDate, error: deleteError, loading: deleteLoading } = useDeleteRequest<Holiday>();

    const {
        makeRequest: getListOfHolidays,
        error: getHolidaysError,
        loading,
        responseData: HolidaysList,
    } = useGetRequest<ListResponse<Holiday>>();

    const getDays = useCallback(async () => {
        try {
            const res = await getListOfHolidays(`/holiday`, {
                params: {
                    assignment: currentAssignment?.id,
                    page: page,
                    pageSize: pageSize,
                },
            });
            setPages(res.data.info.pages);
            setPage(res.data.info.page);
        } catch (error) {
            //handled by hook
        }
    }, [currentAssignment, page, pageSize]);

    const uploadFile = async (file: File) => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await uploadHoliday(`/holiday/upload`, formData, {
                params: { assignment: currentAssignment?.id },
            });
            if (res.data.length > 0) {
                const dateCount = res.data.length;
                const message = `${dateCount} holiday${dateCount !== 1 ? 's' : ''} have been added.`;

                toast({
                    title: 'Holidays Added',
                    description: message,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'No New Holidays',
                    description: 'No new holidays were added from the uploaded file.',
                    status: 'info',
                    duration: 5000,
                    isClosable: true,
                });
            }
            await getDays();
        } catch (error) {
            //handled by hook
        }
    };

    const deleteDate = async () => {
        try {
            if (deleteHolidayDate) {
                await removeHolidayDate(`/holiday/${deleteHolidayDate.id}`, {
                    params: { assignment: currentAssignment?.id },
                });
                setDelete(undefined);
                await getDays();
            }
        } catch (error) {
            //handled by hook
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // re-intialising the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        if (!file) {
            return;
        }

        await uploadFile(file);
    };

    useEffect(() => {
        (async () => {
            if (currentAssignment) {
                await getDays();
            }
        })();
    }, [currentAssignment, page, pageSize]);

    if (getHolidaysError) {
        return (
            <SimpleAlert variant={'left-accent'} w="full">
                <Text>Cannot get list of holidays, please refresh the page and try again</Text>
            </SimpleAlert>
        );
    }

    return (
        <Tile
            collapsable
            colspan={3}
            heading="Holiday Calendar"
            rounded={'lg'}
            borderWidth={1}
            borderColor={'gray.100'}
        >
            <Stack gap={3}>
                <Text>
                    This section is holiday calendar which lists non-working days of UTS. The holiday calendar is used
                    in calculating leave days for a date period.
                </Text>
                <>
                    {uploadError && (
                        <SimpleAlert variant={'left-accent'}>
                            <Text>
                                {(uploadError as AxiosError<{ message: string }>).response?.data?.message ??
                                    'Could not upload the file, please refresh the page and try again'}
                            </Text>
                        </SimpleAlert>
                    )}
                    <Flex justify="flex-start">
                        <Input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            display="none"
                        />
                        <Button
                            leftIcon={<AttachmentIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            isLoading={uploadLoading}
                            disabled={uploadLoading}
                            aria-label="Upload Holiday Calendar"
                        >
                            Upload Holiday Calendar Spreadsheet
                        </Button>
                    </Flex>
                </>
                <Box width="full" overflowX={'auto'}>
                    <Text>Total: {HolidaysList && HolidaysList.info.total}</Text>
                    {loading ? (
                        <LoadingSpinner />
                    ) : (
                        <ListTable
                            tableCaption="List of holiday calendar days"
                            columns={HolidayTableColumns}
                            getActions={(item) => [
                                {
                                    id: 'remove',
                                    label: 'Remove',
                                    icon: <Icons.DeleteIcon />,
                                    event: () => setDelete(item),
                                    ariaLabel: `Remove ${item.date}`,
                                },
                            ]}
                            data={HolidaysList && HolidaysList.items}
                        />
                    )}
                </Box>
                <Box width="full" overflowX={'auto'}>
                    <PaginationToolbar
                        setPageSize={setPageSize}
                        setNewPage={setPage}
                        pageSize={pageSize}
                        pages={pages}
                        page={page}
                        focusPage={focusPage}
                        setFocusPage={setFocusPage}
                    />
                </Box>
            </Stack>
            <Dialog
                isOpen={!!deleteHolidayDate}
                isLoading={deleteLoading}
                dialogHeader={`Delete ${deleteHolidayDate?.date}`}
                dialogConfirmLabel="Delete"
                onClose={() => setDelete(undefined)}
                colorScheme="red"
                errorMsg={deleteError ? 'Could not delete the date, please refresh the page and try again' : undefined}
                dialogAction={deleteDate}
            >
                <Text>Are you sure you want to delete this date?</Text>
            </Dialog>
        </Tile>
    );
};

export default withCurrentAssignment(UploadHolidays);
